import { type MutateOptions, useMutation } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useCallback } from 'react';
import { useEffectiveExperience } from '@/features/experiences/contexts/PageExperienceContext';
import { gqlClient } from '@/lib';
import type { ExperienceLabel, LabelType } from '@/types/gql.generated';
import type { QueryError } from '@/utils/errors';
import { useLabelCache } from './useLabelCache';
import type {
  UpdateExperienceLabelMutation,
  UpdateExperienceLabelMutationVariables,
} from './useUpdateLabel.generated';

const query = gql`
  mutation UpdateExperienceLabel($input: UpdateExperienceLabelInput!) {
    updateExperienceLabel(input: $input) {
      id
      text
      sortOrder
    }
  }
`;

type Context = { prevLabel?: ExperienceLabel };

export const useUpdateLabel = (labelType: LabelType) => {
  const experience = useEffectiveExperience();
  const cache = useLabelCache();

  const { mutate } = useMutation<
    UpdateExperienceLabelMutation,
    QueryError,
    UpdateExperienceLabelMutationVariables,
    Context
  >({
    mutationFn: (variables) =>
      gqlClient.request<
        UpdateExperienceLabelMutation,
        UpdateExperienceLabelMutationVariables
      >(query, variables),
    onMutate: ({ input }) => {
      const prevLabel = cache.getLabel(labelType, input.labelId);
      cache.updateLabel(labelType, { id: input.labelId, text: input.text });
      return { prevLabel };
    },
    onError: (_error, _variables, context) => {
      if (context?.prevLabel) {
        cache.updateLabel(labelType, context.prevLabel);
      }
    },
  });

  const updateLabel = useCallback(
    (
      labelId: string,
      text: string,
      options?: MutateOptions<
        UpdateExperienceLabelMutation,
        QueryError,
        UpdateExperienceLabelMutationVariables,
        Context
      >
    ) =>
      mutate(
        {
          input: {
            experienceId: experience.id,
            labelId,
            text,
          },
        },
        options
      ),
    [mutate, experience.id]
  );

  return { updateLabel };
};
